import { MouseEvent, ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';

import {
	useGetSession,
	useIsAboveTheFold,
	useIsBelowTheFold,
} from '../../../../hooks/session.hooks';
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { EditorSizes } from '../../../../types/template-layouts';
import { IQuestionPromptTranslations, PageModule, PaginatedQuestion, Survey, SurveyType } from '../../../../types/working-model';
import { OptionalComponent } from '../../../../utils/optional-component';
import { useScreenMediaQuery } from '../../../../utils/use-screen-media-query';
import Icon, { ICONS } from '../../../general-ui/icon';
import EngageCardTitle from './engage-card-title';
import EngageCardInfo from './engage-card-info';
import { useGetAnsweredQuestionCount } from './hooks/engage.hooks';
import { getEngageItemByType } from './utils/engage.utils';
import { useIsAdmin } from 'hooks/context.hooks';
import { isSafari } from 'utils/utils';
import { useTranslate } from 'i18n/useTranslationModules';
import useSurveyResponseCount from './hooks/use-survey-response-count';
import { useEngagePanel } from './engage-panel-context';

import './engage-card.scss';
import { useLanguageParam } from '../../utils';

const ANIMATION_TIME = 300;
const OPACITY_TRANSITION_TIME = ANIMATION_TIME + 100;
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface IEngageCardWrapperProps {
	engageCardId: number;
	cardName: string;
	survey?: Survey;
	isSurveyType?: boolean;
	isReviewMode?: boolean;
	editableTitle?: JSX.Element;
	engageType?: SurveyType;
	showResults: boolean;
	children: ReactNode;
	searchComponent?: JSX.Element;
	filterComponent?: JSX.Element;
	className?: string;
	preview?: boolean;
	activeEngageCard?: number;
	order?: number;
	setActiveEngageCard: (state?: number) => void;
	onViewResults?: () => void;
	forceRetake?: () => void;
	qaQuestions?: [number, PaginatedQuestion][]
	isEditor?: boolean;
	module?: PageModule;
	questionPromptTranslations?: IQuestionPromptTranslations;
}

export default function EngageCardWrapper({
	engageCardId,
	cardName,
	survey,
	isSurveyType,
	isReviewMode,
	engageType,
	showResults,
	children,
	filterComponent,
	className = '',
	preview,
	activeEngageCard,
	setActiveEngageCard,
	onViewResults,
	forceRetake,
	qaQuestions,
	isEditor,
	questionPromptTranslations,
	module
}: IEngageCardWrapperProps): JSX.Element {
	const { isLessThan1024 } = useScreenMediaQuery();
	const editorSize = useTypedSelector(state => state.CreateEventReducer.editorSize);
	const engagementResults = useTypedSelector(state => state.SurveysReducer.engagementResults);
	const loadingEngagementResults = useTypedSelector(state => state.SurveysReducer.loadingEngagementResults);
	const navigate = useNavigate();
	const location = useLocation();
	const session = useGetSession();
	const isAboveTheFold = useIsAboveTheFold(session);
	const isBelowTheFold = useIsBelowTheFold(session);
	const answeredQuestionsCount = useGetAnsweredQuestionCount(survey);
	const isAdmin = useIsAdmin();
	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const [totalQuestions, setTotalQuestions] = useState(0);

	const { totalResponses } = useSurveyResponseCount({ questions: survey?.questions });

	const { tagIcon, name } = getEngageItemByType(showResults, engageType);
	const language = useLanguageParam();

	const { t } = useTranslate(['session', 'homepage']);
	const translationUpdates = useTypedSelector(event => event.LiveEventReducer.translationUpdates);

	const translationLabel = useMemo(() => {
		const translatedName = t(`session:${name}`, t(`homepage:${name}`, name));
		if (questionPromptTranslations?.label) {
			if (questionPromptTranslations.label?.[language]) {
				return questionPromptTranslations.label[language];
			}
			if (questionPromptTranslations.label?.base) {
				return questionPromptTranslations.label.base;
			}
		}

		if (!survey?.label) return translatedName;

		return survey.label?.[language] || survey?.label.base || translatedName;

		// requires translationUpdates in the dependency array to update the translation when the language changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [t, name, questionPromptTranslations, language, survey, translationUpdates]) as string;

	useEffect(() => {
		if (qaQuestions) {
			setTotalQuestions(qaQuestions.length);
		}
	}, [engagementResults, qaQuestions, survey]);


	const { questions } = survey || {};

	const maxQuestions = questions?.length || 0;
	const isDesktop = preview ? editorSize === EditorSizes.desktop : !isLessThan1024;
	const isQuestionType = engageType === SurveyType.question_prompt;
	const isCommentBoxType = engageType === SurveyType.comment_box_prompt;
	const surveyCompleted = answeredQuestionsCount === maxQuestions;

	// bump +1 when the user completes the survey for the first time this session
	const initialCompleted = useRef(surveyCompleted);
	const [localBump, setLocalBump] = useState(0);

	useEffect(() => {
		if (surveyCompleted && !initialCompleted.current) {
			setLocalBump(1);
		}
	}, [surveyCompleted]);

	const privateResponsesEnabled = isQuestionType && module
		? module.modules?.[0].settings.private_responses === true
		: false;

	const expanded = activeEngageCard === engageCardId;

	const timeout = useRef<NodeJS.Timeout | null>(null);
	const [isAnimating, setIsAnimating] = useState(false);
	const cardRef = useRef<HTMLDivElement>(null);
	const pendingFocus = useRef<'body' | 'opener' | null>(null);

	// keyboard-triggered clicks have detail 0
	const handleOpen = (event?: MouseEvent) => {
		if (!expanded && event?.detail === 0) {
			pendingFocus.current = 'body';
		}

		if (!expanded) {
			setIsAnimating(true);
			timeout.current && clearTimeout(timeout.current);
			timeout.current = setTimeout(() => setIsAnimating(false), ANIMATION_TIME);
		}

		if (activeEngageCard) setActiveEngageCard();
		setActiveEngageCard(engageCardId);

		// push an entry onto the current history state so the back button can trigger the close
		// but only if desktop, because on tablet and mobile we drop down to btf and there's no need to navigate there
		// or the page will scroll up on every click
		// skip in editor/preview mode to avoid polluting admin panel navigation history
		if (isAboveTheFold && isDesktop && !isEditor) {
			navigate(location.pathname, { state: { engageCardId } });
		}
	};

	const handleClose = (event: MouseEvent) => {
		event.stopPropagation();

		if (event.detail === 0) {
			pendingFocus.current = 'opener';
		}

		setIsAnimating(true);
		timeout.current && clearTimeout(timeout.current);
		timeout.current = setTimeout(() => setIsAnimating(false), ANIMATION_TIME / 2); // halving the time prevents input flashing

		setActiveEngageCard();
		setIsSearchOpen?.(false);

		if (isAdmin && forceRetake) {
			forceRetake();
		}

		if ((location.state as any)?.engageCardId) {
			navigate(-1);
		}
	};

	// In v7 we use a location-change effect to replicate history.listen POP detection
	const prevPathnameRef = useRef(location.pathname);
	useEffect(() => {
		const isPopNavigation = prevPathnameRef.current !== location.pathname;
		if (isPopNavigation && isAboveTheFold) {
			setActiveEngageCard();
			setIsSearchOpen?.(false);

			if (isAdmin && forceRetake) {
				forceRetake();
			}
		}
		prevPathnameRef.current = location.pathname;
	}, [location, setActiveEngageCard, isAboveTheFold, isAdmin, forceRetake]);

	useEffect(() => {
		if (isAnimating || !pendingFocus.current) return;

		const focusTarget = pendingFocus.current;
		pendingFocus.current = null;
		let frame = 0;
		let attempts = 0;

		// The body isn't in the DOM yet when this runs (OptionalComponent shows it one render later), so retry for a few frames.
		// <Activity /> would avoid this, but it keeps the body mounted while hidden, which can drop a question card's in-progress comment fetch.
		const tryFocus = () => {
			const card = cardRef.current;
			const target = focusTarget === 'body'
				? card?.querySelector<HTMLElement>(`.engage-card-body :is(${FOCUSABLE})`)
				: card?.querySelector<HTMLElement>('.engage-card-hover-action');

			if (!target && attempts++ < 10) {
				frame = requestAnimationFrame(tryFocus);
				return;
			}

			(target ?? card?.querySelector<HTMLElement>('.engage-card-header-action'))?.focus();
		};

		tryFocus();

		return () => {
			cancelAnimationFrame(frame);
		};
	}, [isAnimating, expanded]);

	const isAboveTheFoldExpanded = isAboveTheFold && expanded;
	const showArrowBack = isAboveTheFoldExpanded && isDesktop;

	const engagePanel = useEngagePanel();
	const setOpenCardId = engagePanel?.setOpenCardId;
	const panelCardId = useId();
	const coversPanel = isAboveTheFoldExpanded && isDesktop;
	const isCoveredByOpenCard = !!engagePanel?.openCardId && engagePanel.openCardId !== panelCardId;

	useEffect(() => {
		if (!setOpenCardId || !coversPanel) return;

		setOpenCardId(panelCardId);
		return () => {
			setOpenCardId(current => current === panelCardId ? null : current);
		};
	}, [setOpenCardId, coversPanel, panelCardId]);
	const showCloseButton = (isBelowTheFold && expanded) || (isAboveTheFoldExpanded && !isDesktop);
	const renderBody = isBelowTheFold || !isDesktop;

	const handleToggleSearch = () => setIsSearchOpen?.((prev) => !prev);

	const searchTrigger = <button
		className={classNames('engage-card-search-trigger no-padding no-style', { active: isSearchOpen })}
		onClick={handleToggleSearch}
	>
		<Icon name={ICONS.SEARCH} color="" size={16} />
	</button>;

	return (
		<div
			className={classNames('engage-card', className, {
				'engage-card-survey': isSurveyType,
				'engage-card-review-mode': isReviewMode,
				'engage-card-expanded': expanded,
				'engage-card-disabled': false,
				'engage-card-search-open': isSearchOpen,
				'is-editor': isEditor,
				'is-safari': isSafari,
			})}
			onClick={handleOpen}
			ref={cardRef}
			inert={isCoveredByOpenCard}
		>
			<div
				className="engage-card-header"
				style={{
					visibility: isAnimating && !expanded ? 'hidden' : 'visible',
					opacity: isAnimating && !expanded ? '0' : '1',
					transition: `opacity ${OPACITY_TRANSITION_TIME}ms ease-in-out`
				}}
			>
				<div className="engage-card-header-top">
					<EngageCardTitle
						name={translationLabel}
						tagIcon={tagIcon}
						cardName={cardName}
						isQuestionType={isQuestionType}
						isCommentBoxType={isCommentBoxType}
						surveyCompleted={surveyCompleted}
						searchTrigger={searchTrigger}
						filterComponent={filterComponent}
						onViewResults={onViewResults}
						handleOpen={handleOpen}
						privateResponsesEnabled={privateResponsesEnabled && expanded}
					/>
					<OptionalComponent display={showCloseButton || showArrowBack}>
						<button
							className="engage-card-header-action no-style no-padding"
							onClick={handleClose}
							aria-label={showCloseButton ? t('session:Close', 'Close') : t('session:Back', 'Back')}
						>
							{showCloseButton ?
								<Icon name={ICONS.CLOSE} color="" size={16} /> :
								<Icon name={ICONS.ARROW_LEFT} color="" size={16} />
							}
						</button>
					</OptionalComponent>
				</div>

				<OptionalComponent display={!expanded}>
					<EngageCardInfo
						engageType={engageType}
						surveyCompleted={surveyCompleted}
						answeredQuestionsCount={answeredQuestionsCount}
						maxQuestions={maxQuestions}
						totalResponses={totalResponses + localBump}
						totalQuestions={totalQuestions}
						miscTranslations={survey?.misc_translations}
						privateResponsesEnabled={privateResponsesEnabled}
						loading={loadingEngagementResults}
					/>
				</OptionalComponent>
			</div>

			<OptionalComponent display={!isAnimating}>
				<>
					{renderBody ? children : expanded && children}
				</>
			</OptionalComponent>

			<OptionalComponent display={!isAnimating}>
				<>
					<div className="scroll-overlay-top" />
					<div className="scroll-overlay-bottom" />
				</>
			</OptionalComponent>
		</div>
	);
}
